import { useContext, useEffect, useState } from "react";
import { FORMNAMES } from "../utilities/codes";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { APIENDPOINT } from "../services/apiHelper";
import toast from "react-hot-toast";
import { schoolContext } from "../contexts/SchoolContextProvider";
import { mediaSource } from "../utilities/mediautilities";
import { getFormEntriesByFormName } from "../services/formServices";
import { FaArrowRight, FaBook, FaBriefcase, FaGraduationCap, FaTrophy } from "react-icons/fa";
import { MdEmail } from "react-icons/md";
import { useTranslation } from "react-i18next";
import { I18N_KEYS } from "../languages/PlaceHolder.js";

const TopTeachers = () => {
    const [topTeachers, setTopTeachers] = useState([]);
    const [selectedTeacher, setSelectedTeacher] = useState(null);
    const { schoolDetails } = useContext(schoolContext);
    const { t } = useTranslation()
    const navigate = useNavigate();

    useEffect(() => {
        getTopTeachers();
    }, []);

    const getTopTeachers = async () => {
        try {
            const filters = { staff_role: { operator: "equals", value: "Teacher" }, top_teacher: { operator: "equals", value: true } }
            const response = await getFormEntriesByFormName(FORMNAMES.STAFFINFORMATION, filters);
            if (response && response.length > 0) {
                setTopTeachers(response);
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    return (
        <>
            {topTeachers?.length > 0 &&
                <section
                    id="top-teachers"
                    className="py-12 md:py-16 px-4 md:px-6 bg-gray-50"
                >
                    <div className="max-w-7xl mx-auto">
                        {/* Heading */}
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="text-center mb-10"
                        >
                            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-2">
                                {t(I18N_KEYS.TOPTEACHERTITLE)}
                            </h2>
                            <div className="w-20 h-1 bg-secondary mx-auto mb-3"></div>
                            {schoolDetails?.teacher_subtitle &&
                                <p className="text-gray-600 text-sm md:text-base">
                                    {schoolDetails?.teacher_subtitle}
                                </p>
                            }
                        </motion.div>

                        {/* Teachers - Card Grid Layout */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 common-container">
                            {topTeachers?.map((teacher, index) => (
                                <motion.div
                                    key={index}
                                    initial={{ opacity: 0, y: 30 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: index * 0.1 }}
                                    whileHover={{ y: -5 }}
                                    className="group cursor-pointer"
                                    onClick={() => setSelectedTeacher(teacher)}
                                >
                                    {/* Teacher Card */}
                                    <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 h-full flex flex-col border border-gray-100">
                                        {/* Image Section */}
                                        <div className="relative h-56 bg-gradient-to-br from-primary/10 to-secondary/10 overflow-hidden">
                                            <img
                                                src={mediaSource(teacher?.payload?.profile_photo?.fileUrl)}
                                                alt={teacher?.payload?.full_name || "Teacher"}
                                                onError={(e) => mediaSource(e)}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                            />
                                            {/* Gradient Overlay */}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>

                                            {/* Subject Badge */}
                                            {teacher?.payload?.subjects_assigned1_ref?.length > 0 && (
                                                <div className="absolute top-4 left-4">
                                                    <span className="px-3 py-1.5 bg-primary text-accent text-sm font-semibold rounded-full shadow-lg">
                                                        {teacher?.payload?.subjects_assigned1_ref?.[0]?.name}
                                                    </span>
                                                </div>
                                            )}
                                        </div>

                                        {/* Content Section */}
                                        <div className="p-5 flex-1 flex flex-col">
                                            {/* Name */}
                                            {teacher?.payload?.full_name && (
                                                <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-primary transition-colors">
                                                    {teacher?.payload?.full_name}
                                                </h3>
                                            )}

                                            {/* Quick Info */}
                                            <div className="space-y-2 mb-4 flex-1">
                                                {teacher?.payload?.qualification && (
                                                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                                                        <span className="text-base"><FaGraduationCap /></span>
                                                        <span className="line-clamp-1">{teacher?.payload?.qualification.split(',')[0]}</span>
                                                    </div>
                                                )}
                                                {teacher?.payload?.experience && (
                                                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                                                        <span className="text-base"><FaBriefcase /></span>
                                                        <span>{teacher?.payload?.experience} {t(I18N_KEYS.TEACHEREXP)}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* View Profile Link */}
                                            <div className="mt-auto pt-3 border-t border-gray-100">
                                                <span className="text-primary text-sm font-semibold hover:underline flex items-center gap-1">
                                                    {t(I18N_KEYS.VIEWPROFILE)}
                                                    <span><FaArrowRight /></span>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </div>

                        {/* See More Button */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            whileInView={{ opacity: 1 }}
                            viewport={{ once: true }}
                            className="text-center"
                        >
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => navigate('/topteachers')}
                                className="px-8 py-3 bg-primary text-accent rounded-lg font-semibold text-sm md:text-base hover:bg-primary/90 transition-all shadow-md hover:shadow-lg inline-flex items-center gap-2"
                            >
                                <span>{t(I18N_KEYS.VIEWTEACHERBUTTON)}</span>
                                <span><FaArrowRight /></span>
                            </motion.button>
                        </motion.div>
                    </div>

                    {/* Modal for detailed view */}
                    <AnimatePresence>
                        {selectedTeacher && (
                            <motion.div
                                className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() => setSelectedTeacher(null)}
                            >
                                <button
                                    onClick={() => setSelectedTeacher(null)}
                                    className="absolute top-4 right-4 text-accent text-3xl font-bold hover:text-primary z-10 w-12 h-12 flex items-center justify-center bg-black/50 rounded-full hover:bg-black/70 transition-colors"
                                >
                                    &times;
                                </button>
                                <motion.div
                                    className="bg-accent rounded-3xl overflow-hidden shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
                                    initial={{ scale: 0.8, y: 40 }}
                                    animate={{ scale: 1, y: 0 }}
                                    exit={{ scale: 0.8, y: 40 }}
                                    transition={{ duration: 0.3 }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="p-8 md:p-12">
                                        {/* Teacher Photo */}
                                        <div className="flex justify-center mb-8">
                                            <div className="relative w-48 h-48 md:w-64 md:h-64">
                                                <div className="absolute inset-0 bg-gradient-to-br from-primary via-secondary to-primary rounded-full p-1">
                                                    <div className="w-full h-full bg-accent rounded-full overflow-hidden border-4 border-accent">
                                                        <img
                                                            src={mediaSource(selectedTeacher?.payload?.profile_photo?.fileUrl)}
                                                            alt={selectedTeacher?.payload?.full_name || "Teacher"}
                                                            className="w-full h-full object-cover"
                                                            onError={(e) => mediaSource(e)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Teacher Details */}
                                        <div className="text-center">
                                            {selectedTeacher?.payload?.full_name &&
                                                <h3 className="text-3xl md:text-4xl font-bold mb-3 text-gray-800">
                                                    {selectedTeacher?.payload?.full_name}
                                                </h3>
                                            }
                                            {selectedTeacher?.payload?.subjects_assigned1_ref?.length > 0 &&
                                                <div className="inline-flex items-center gap-2 px-6 py-2 bg-primary/10 rounded-full mb-6">
                                                    {selectedTeacher?.payload?.subjects_assigned1_ref?.map((subject,idx) => (
                                                        <span key={idx} className="text-primary font-semibold text-lg">
                                                            {subject?.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            }

                                            {/* Info Grid */}
                                            <div className="grid md:grid-cols-2 gap-6 mb-8">
                                                {selectedTeacher?.payload?.qualification && (
                                                    <div className="bg-gray-50 rounded-xl p-4">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <span className="text-2xl"><FaGraduationCap /></span>
                                                            <h4 className="font-semibold text-gray-700">{t(I18N_KEYS.TEACHERQUALIFICATION)}</h4>
                                                        </div>
                                                        <p className="text-gray-600">{selectedTeacher?.payload?.qualification}</p>
                                                    </div>
                                                )}
                                                {selectedTeacher?.payload?.experience && (
                                                    <div className="bg-gray-50 rounded-xl p-4">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <span className="text-2xl"><FaBriefcase /></span>
                                                            <h4 className="font-semibold text-gray-700">{t(I18N_KEYS.TEACHEREXP)}</h4>
                                                        </div>
                                                        <p className="text-gray-600">{selectedTeacher?.payload?.experience}</p>
                                                    </div>
                                                )}
                                                {selectedTeacher?.payload?.specialization && (
                                                    <div className="bg-gray-50 rounded-xl p-4">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <span className="text-2xl"><FaBook /></span>
                                                            <h4 className="font-semibold text-gray-700">{t(I18N_KEYS.TEACHERSPECIALIZATION)}</h4>
                                                        </div>
                                                        <p className="text-gray-600">{selectedTeacher?.payload?.specialization}</p>
                                                    </div>
                                                )}
                                                {selectedTeacher?.payload?.achievement && (
                                                    <div className="bg-gray-50 rounded-xl p-4">
                                                        <div className="flex items-center gap-3 mb-2">
                                                            <span className="text-2xl text-primary"><FaTrophy /></span>
                                                            <h4 className="font-semibold text-secondary">{t(I18N_KEYS.ACHIEVEMENT)}</h4>
                                                        </div>
                                                        <p className="text-secondary font-medium">{selectedTeacher?.payload?.achievement}</p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Description */}
                                            {selectedTeacher?.payload?.about_staff && (
                                                <div className="bg-primary/5 rounded-xl p-6 mb-6">
                                                    <h4 className="font-semibold text-primary mb-3 text-left">{t(I18N_KEYS.ABOUT)}</h4>
                                                    <p className="text-gray-700 leading-relaxed text-left"
                                                        dangerouslySetInnerHTML={{
                                                            __html: selectedTeacher?.payload?.about_staff
                                                        }}
                                                    >
                                                    </p>
                                                </div>
                                            )}

                                            {/* Email */}
                                            {selectedTeacher?.payload?.email && (
                                                <div className="flex items-center justify-center gap-2 text-gray-600 text-primary">
                                                    <span><MdEmail /></span>
                                                    <a
                                                        href={`mailto:${selectedTeacher?.payload?.email}`}
                                                        className="text-primary hover:underline"
                                                    >
                                                        {selectedTeacher?.payload?.email}
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </section>
            }
        </>
    );
}

export default TopTeachers;